import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Snackbar,
  Alert,
} from '@mui/material';
import FilledButton from './FilledButton';
import OutlinedButton from './OutlinedButton';
import { changeEmail, checkEmailExists } from '../services/api';
import { useNavigate } from 'react-router-dom';

export default function ChangeEmailDialog({ open, onClose, onSuccess }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [emailExists, setEmailExists] = useState(false);
  const [checking, setChecking] = useState(false);
  useEffect(() => {
    if (open) {
      setEmail('');
      setErrorMessage('');
      setEmailExists(false);
    }
  }, [open]);

  const checkDuplicate = async () => {
    if (!email) return;
    setChecking(true);
    try {
      const exists = await checkEmailExists(email);
      setEmailExists(exists);
      if (exists) setErrorMessage('Email already exists.');
      else setErrorMessage('');
    } catch (err) {
      console.error('Failed to check email', err);
    } finally {
      setChecking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    if (emailExists) {
      return;
    }
    setSubmitting(true);
    try {
      await checkDuplicate();
      if (emailExists) {
        setSubmitting(false);
        return;
      }
      await changeEmail(email);
      const msg = 'Email updated. Please verify your new address.';
      if (onSuccess) onSuccess(msg);
      onClose();
      navigate('/verify-email-sent', { state: { email } });
    } catch (err) {
      setErrorMessage(err.response?.data?.detail || 'Failed to update email.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{ component: 'form', onSubmit: handleSubmit }}
      >
        <DialogTitle>Change Email</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2">
            Changing your email will require verification. Some features will be disabled until you verify the new address.
          </Typography>
          <div>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
              New Email
            </Typography>
            <TextField
              size="small"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailExists(false);
              }}
              onBlur={checkDuplicate}
              error={emailExists}
              helperText={emailExists ? 'Email already exists.' : ''}
              required
              fullWidth
            />
          </div>
        </DialogContent>
        <DialogActions>
          <OutlinedButton onClick={onClose} text="Cancel" size="small" color="primary" />
          <FilledButton type="submit" text="Confirm" size="small" color="primary" disabled={submitting || !email} />
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorMessage('')} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
